import { useState } from "react";
import { Star } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero } from "@/components/common/Bits";
import { Reveal } from "@/components/motion/Reveal";
import { Spinner, EmptyState } from "@/components/common/States";
import { useCategories, useTestimonials } from "@/hooks/useContent";

export default function Testimonials() {
    const { data: items, loading } = useTestimonials();
    const [type, setType] = useState("All");
    const TYPES = useCategories(items, "type");

    const filtered = type === "All" ? items : items.filter((t) => t.type === type);

    return (
        <>
            <SEO
                title="Testimonials — What Our Customers Say"
                path="/testimonials"
                description="Read what homeowners, landlords, tenants, agents and businesses say about ProCare.SG's one-stop property care."
            />
            <PageHero
                label="Testimonials"
                title="Trusted for Every MOVE"
                subtitle="Homeowners, landlords, tenants, agents and businesses share their ProCare.SG experience."
                image="/images/movein-prep.webp"
            />

            <section className="py-16">
                <div className="mx-auto max-w-7xl px-6">
                    <div className="flex flex-wrap gap-2">
                        {TYPES.map((t) => (
                            <button
                                key={t}
                                onClick={() => setType(t)}
                                data-testid={`testimonial-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${type === t ? "bg-brand-red text-white" : "bg-brand-surface text-brand-blue hover:bg-brand-blue/10"}`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>

                    <div className="mt-12">
                        {loading ? (
                            <div className="flex justify-center py-16"><Spinner /></div>
                        ) : filtered.length ? (
                            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                                {filtered.map((t, i) => (
                                    <Reveal key={t.id} delay={(i % 3) * 0.06}>
                                        <div className="flex h-full flex-col rounded-3xl border border-brand-blue/10 bg-white p-7 shadow-brand">
                                            {t.rating && (
                                                <div className="flex gap-1">
                                                    {[...Array(t.rating)].map((_, j) => (
                                                        <Star key={j} className="h-4 w-4 fill-brand-red text-brand-red" />
                                                    ))}
                                                </div>
                                            )}
                                            <p className="mt-4 flex-1 text-brand-ink/80">“{t.content}”</p>
                                            <div className="mt-6 flex items-center gap-3">
                                                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-blue font-display font-semibold text-white">{t.name.charAt(0)}</div>
                                                <div>
                                                    <p className="font-semibold text-brand-ink">{t.name}</p>
                                                    <p className="text-xs text-brand-ink/65">{t.type}{t.service ? ` · ${t.service}` : ""}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </Reveal>
                                ))}
                            </div>
                        ) : (
                            <EmptyState title="No testimonials in this category" />
                        )}
                    </div>
                </div>
            </section>
        </>
    );
}
